import React, { useEffect, useState } from 'react';

import { Form } from 'antd';
import styled from 'styled-components';

import Button from 'components/lib/Button';
import Input from 'components/lib/Input';
import Modal from 'components/lib/Modal';
import TextArea from 'components/lib/TextArea';

import ActionPlanEmailCampaignDelayFields from './ActionPlanEmailCampaignDelayFields';
import ActionPlanEmailCampaignMessageDeleteConfirmModal from './ActionPlanEmailCampaignMessageDeleteConfirmModal';

export type ActionPlanCreateEditEmailCampaignMessageValues = {
  delayEnabled: boolean;
  delayDays: number;
  delayHours: number;
  delayMinutes: number;
  subject: string;
  content: string;
};

type Props = {
  open: boolean;
  isEditing?: boolean;
  initialValues?: Partial<ActionPlanCreateEditEmailCampaignMessageValues>;
  isSaving?: boolean;
  isDeleting?: boolean;
  onCancel: () => void;
  onSave: (values: ActionPlanCreateEditEmailCampaignMessageValues) => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
};

const StyledModal = styled(Modal)`
  &&& {
    .ant-modal-body {
      padding: 0 30px;
    }
  }
`;

const Body = styled.div`
  padding: 0 0 20px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const EmailBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--active-blue);
  width: 46px;
  height: 16px;
  color: var(--white);
  border-radius: 10px;
  font-size: 9px;
  line-height: 1;
`;

const HeaderDescription = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

const SectionLabel = styled.label`
  display: block;
  margin: 10px 0 8px;
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const SubjectInput = styled(Input)`
  &&& {
    height: 36px;
  }
`;

const ContentTextArea = styled(TextArea)`
  &&& {
    height: 215px;
    min-height: 215px;
    color: var(--text-primary);
  }

  &&&.mini-scroll::-webkit-scrollbar-thumb {
    height: 10px;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FooterButton = styled(Button)`
  min-width: 120px;
`;

const DeleteMessageButton = styled(FooterButton)`
  &&& {
    color: #ff6f45;
    border-color: #ff6f45;
    background: var(--white);
  }
`;

const DEFAULT_VALUES: ActionPlanCreateEditEmailCampaignMessageValues = {
  delayEnabled: false,
  delayDays: 1,
  delayHours: 0,
  delayMinutes: 0,
  subject: '',
  content: '',
};

const ActionPlanCreateEditEmailCampaignMessageModal = ({
  open,
  isEditing = false,
  initialValues,
  isSaving = false,
  isDeleting = false,
  onCancel,
  onSave,
  onDelete,
}: Props) => {
  const [form] = Form.useForm<ActionPlanCreateEditEmailCampaignMessageValues>();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const modalTitle = isEditing ? 'Edit Message' : 'Add Message';

  useEffect(() => {
    if (!open) {
      return;
    }

    form.setFieldsValue({
      ...DEFAULT_VALUES,
      ...initialValues,
    });
  }, [form, initialValues, open]);

  const delayEnabled = Form.useWatch('delayEnabled', form) ?? false;
  const currentSubject = Form.useWatch('subject', form) ?? '';

  const deleteDescription = `You are about to delete the message with the subject '${currentSubject || ''}'. This cannot be undone.`;

  const handleSave = async () => {
    try {
      const validatedValues = await form.validateFields();
      await onSave(validatedValues);
    } catch (error: any) {
      if (Array.isArray(error?.errorFields)) {
        return;
      }

      throw error;
    }
  };

  const handleDelete = async () => {
    if (!onDelete) {
      return;
    }

    await onDelete();
    setDeleteConfirmOpen(false);
  };

  return (
    <>
      <StyledModal
        className="compact bare-header"
        open={open}
        centered
        onCancel={onCancel}
        destroyOnClose
        title={modalTitle}
        width={600}
        footer={
          <Footer>
            <div>
              {isEditing && onDelete ? (
                <DeleteMessageButton onClick={() => setDeleteConfirmOpen(true)} disabled={isSaving || isDeleting}>
                  Delete Message
                </DeleteMessageButton>
              ) : null}
            </div>
            <FooterRight>
              <FooterButton type="primary" loading={isSaving} onClick={handleSave}>
                Save
              </FooterButton>
              <FooterButton onClick={onCancel} disabled={isSaving}>
                Cancel
              </FooterButton>
            </FooterRight>
          </Footer>
        }
      >
        <Body>
          <HeaderRow>
            <EmailBadge>EMAIL</EmailBadge>
            <HeaderDescription>Edit message subject, main text and timing below.</HeaderDescription>
          </HeaderRow>

          <Form form={form} layout="vertical">
            <ActionPlanEmailCampaignDelayFields delayEnabled={delayEnabled} />

            <SectionLabel>Subject</SectionLabel>
            <Form.Item
              name="subject"
              rules={[{ required: true, message: 'Subject is required' }]}
              style={{ marginBottom: 8 }}
            >
              <SubjectInput />
            </Form.Item>

            <SectionLabel>Content</SectionLabel>
            <Form.Item
              name="content"
              rules={[{ required: true, message: 'Content is required' }]}
              style={{ marginBottom: 0 }}
            >
              <ContentTextArea className="mini-scroll" />
            </Form.Item>
          </Form>
        </Body>
      </StyledModal>

      <ActionPlanEmailCampaignMessageDeleteConfirmModal
        open={deleteConfirmOpen}
        description={deleteDescription}
        isDeleting={isDeleting}
        onCancel={() => setDeleteConfirmOpen(false)}
        onDelete={handleDelete}
      />
    </>
  );
};

export default ActionPlanCreateEditEmailCampaignMessageModal;
